import moment from 'moment';

export const DEMO_STORAGE_KEY = 'clinic-demo-data-v1';
const date = (offset = 0) => moment().add(offset, 'days').format('YYYY-MM-DD');
const clone = value => JSON.parse(JSON.stringify(value));
const success = (data, extra = {}) => ({ status: 'Success', msg: 'Saved in demo mode', data, ...extra });
const failed = msg => ({ status: 'Failed', msg });

export function createDemoData() {
  const doctors = [
    { uid: 'doctor-1', name: 'Dr. Maya Menon', hospital: 'Demo Care Centre', mobile: '9000000001' },
    { uid: 'doctor-2', name: 'Dr. Arun Das', hospital: 'Demo Wellness Clinic', mobile: '9000000002' },
  ];
  const patients = ['Ananya Nair', 'Rohan Kumar', 'Meera Das', 'Vivek Menon', 'Sara Thomas', 'Dev Patel'].map((name, i) => ({
    uid: `patient-${i + 1}`, first_name: name.split(' ')[0], last_name: name.split(' ')[1],
    name, dob: `${1985 + i * 3}-04-12`, age: 41 - i * 3, gender: i % 2 ? 'Male' : 'Female',
    mobile: `900000001${i}`, whatsapp: '', email: `patient${i + 1}@example.com`,
    address: 'Sample address, Kochi', doctor_uid: doctors[i % 2].uid, status: 'active', timestamp: date(-10),
  }));
  const appointments = patients.map((p, i) => ({
    uid: `appointment-${i + 1}`, id: `appointment-${i + 1}`, patient_uid: p.uid,
    appt_title: i % 2 ? 'OT' : 'ST', title: p.name, start_date: date(i - 1),
    start_time: '1000', end_time: '1045', start: `${date(i - 1)} 1000`, end: `${date(i - 1)} 1045`,
    status: i === 0 ? 'completed' : 'active', consultation_fee: 600,
    note: 'Sample therapy session', provisional_diagnosis: 'Demo assessment', in_progress_color: '#dff9ec',
    invoiceUid: i === 0 ? 'invoice-1' : '', timestamp: date(-2),
  }));
  const labels = [{ uid: 'label-1', id: 'label-1', name: 'Hearing aid', short_form: 'HA', color: '#008000' },
    { uid: 'label-2', id: 'label-2', name: 'Therapy', short_form: 'TH', color: '#5a8dee' }];
  return {
    patients, doctors, appointments, labels,
    users: [{ uid: 'user-1', username: 'Demo Admin', email: 'demo@example.com', type: 'admin', status: 'active', timestamp: date(-30) }],
    enquiries: ['Kiran Shah', 'Priya Rao', 'Nikhil Roy', 'Leena Paul'].map((name, i) => ({
      uid: `enquiry-${i + 1}`, name, mobile: `900000002${i}`, whatsapp: '', location: 'Kochi',
      label: [labels[i % 2]], note: 'Interested in a consultation', status: i % 2 ? 'followup' : 'enquiry',
      received_date: date(-i - 1), next_follow_up: date(i), timestamp: date(-i - 1), patient_uid: '',
    })),
    consultationNotes: [{ id: 'note-1', uid: 'note-1', patient_uid: 'patient-1', note: 'Sample initial assessment. Follow-up session recommended.', timestamp: date(-1), added_by: 'Demo Admin' }],
    enquiryNotes: [{ id: 'enquiry-note-1', uid: 'enquiry-note-1', enquiry_uid: 'enquiry-1', note: 'Requested a demonstration.', timestamp: date(-1), added_by: 'Demo Admin' }],
    invoices: [{ uid: 'invoice-1', invoice_no: 'DEMO-1001', patient_uid: 'patient-1', appointment_uid: 'appointment-1',
      invoice_date: date(-1), total: 600, payment_method: 'cash_payment', consultant_detail: 'Dr. Maya Menon', note: 'Sample invoice', timestamp: date(-1),
      invoice_item: [{ id: 'item-1', item: 'Speech therapy session', price: 600, quantity: 1, discount: 0, gst: 0 }] }],
    incomeExpenses: [{ uid: 'income-1', title: 'Therapy session', amount: 600, type: 'income', patient_uid: 'patient-1', added_date: date(-1) },
      { uid: 'expense-1', title: 'Clinic supplies', amount: 250, type: 'expense', patient_uid: '', added_date: date() }],
    products: [{ uid: 'product-1', name: 'Demo hearing aid', price: 25000, discount: 0, image: '', category_id: 'category-1', status: 'active' }],
    categories: [{ uid: 'category-1', name: 'Hearing aids' }], productEnquiries: [],
  };
}

// Seed each visited date once. Stable IDs and the day ledger preserve edits,
// moved appointments and removals when a range is revisited or the app reloads.
function ensureAppointmentRange(db, startDate, endDate) {
  const start = moment(startDate, 'YYYY-MM-DD', true);
  const end = moment(endDate, 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return;
  db.appointmentSeedDays = db.appointmentSeedDays || {};
  const slots = [
    ['0900', '0945', 'active', 'Speech Therapy'],
    ['1100', '1200', 'completed', 'Occupational Therapy'],
    ['1400', '1445', 'active', 'Hearing Assessment'],
    ['1630', '1715', 'cancelled', 'Speech Therapy'],
  ];
  for (const day = start.clone(); day.isBefore(end); day.add(1, 'day')) {
    const key = day.format('YYYY-MM-DD');
    if (db.appointmentSeedDays[key] || !db.patients.length) continue;
    slots.forEach(([from, to, status, title], index) => {
      const patient = db.patients[(day.dayOfYear() + index) % db.patients.length];
      const uid = `demo-appointment-${key}-${index}`;
      db.appointments.push({ uid, id: uid, patient_uid: patient.uid,
        appt_title: title, start_date: key, start_time: from, end_time: to,
        start: `${key} ${from}`, end: `${key} ${to}`, status,
        consultation_fee: index === 2 ? 900 : 600,
        note: 'Sample appointment — you can edit this demo record.',
        provisional_diagnosis: index === 2 ? 'Hearing evaluation' : 'Therapy follow-up',
        reason: status === 'cancelled' ? 'Patient requested another date (demo)' : '',
        in_progress_color: index === 2 ? '#8f25aa' : '#dff9ec', timestamp: day.toISOString(),
      });
    });
    db.appointmentSeedDays[key] = true;
  }
}

function readDatabase() {
  const saved = localStorage.getItem(DEMO_STORAGE_KEY);
  if (saved) {
    try { return JSON.parse(saved); } catch (_) { /* Restore malformed demo data. */ }
  }
  const db = createDemoData();
  localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(db));
  return db;
}

export function resetDemoData() {
  localStorage.removeItem(DEMO_STORAGE_KEY);
  localStorage.removeItem('persist:demo-root');
  localStorage.removeItem('persist:demo-userData');
}

const collections = {
  patient: ['patients', 'patientUid'], doctor: ['doctors', 'doctorUid'], user: ['users', 'userUid'],
  appointment: ['appointments', 'appointmentUid'], consultation_note: ['consultationNotes', 'consultationNoteId'],
  enquiry_note: ['enquiryNotes', 'enquiryNoteId'], enquiry: ['enquiries', 'enquiryUid'], label: ['labels', 'labelUid'],
  income_expense: ['incomeExpenses', 'incomeExpenseUid'], product_category: ['categories', 'productCategoryUid'],
  product: ['products', 'productUid'], product_enquiry: ['productEnquiries', 'productEnquiryUid'], invoice: ['invoices', 'invoiceUid'],
};

function enrich(db, kind, row) {
  if (!row) return null;
  const patient = db.patients.find(p => p.uid === row.patient_uid);
  const doctor = db.doctors.find(d => d.uid === (row.doctor_uid || patient?.doctor_uid));
  if (kind === 'patient') return { ...row, name: `${row.first_name} ${row.last_name || ''}`.trim(), doctor_name: doctor?.name, doctor_mobile: doctor?.mobile, doctor_hospital: doctor?.hospital };
  if (kind === 'appointment') {
    const invoice = db.invoices.find(i => i.appointment_uid === row.id);
    row = { ...row, invoice_uid: invoice?.uid || '', invoice_id: invoice?.invoice_no || '' };
  }
  if (kind === 'invoice') return { ...row, patient: patient || {}, doctor: doctor || {} };
  if (kind === 'appointment' || kind === 'income_expense') return { ...row, first_name: patient?.first_name, last_name: patient?.last_name, title: kind === 'appointment' ? `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() : row.title };
  return row;
}

function normalize(db, kind, input, existing = {}) {
  const row = { ...existing };
  Object.entries(input).forEach(([key, value]) => { row[key.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`)] = value; });
  if (kind === 'consultation_note') row.note = input.consultationNote ?? row.note;
  if (kind === 'patient') { row.doctor_uid = input.referredBy ?? row.doctor_uid; row.status = row.status || 'active'; }
  if (kind === 'enquiry') { row.next_follow_up = input.nextFollowup ?? row.next_follow_up; row.label = input.label || row.label || []; }
  if (kind === 'appointment') {
    row.appt_title = input.title ?? row.appt_title;
    row.start = `${row.start_date} ${row.start_time}`;
    row.end = `${row.start_date} ${row.end_time}`;
    row.in_progress_color = input.inProgressColor ?? row.in_progress_color ?? '#dff9ec';
    row.invoiceUid = existing.invoiceUid || '';
  }
  delete row.password;
  return row;
}

function filtered(rows, input, kind) {
  const search = String(Object.entries(input).find(([k]) => k.startsWith('search'))?.[1] || '').toLowerCase();
  return rows.filter(row => {
    if (search && !JSON.stringify(row).toLowerCase().includes(search)) return false;
    if (input.patientUid && row.patient_uid !== input.patientUid) return false;
    if (input.doctorUid && row.doctor_uid !== input.doctorUid) return false;
    if (input.enquiryUid && row.enquiry_uid !== input.enquiryUid) return false;
    if (input.status) {
      const statuses = input.status.startsWith?.('[') ? JSON.parse(input.status) : [input.status];
      if (!statuses.includes(row.status)) return false;
    }
    if (input.labelFilter && !row.label?.some(l => (l.id || l.uid) === input.labelFilter)) return false;
    const day = row.start_date || row.added_date || row.invoice_date;
    if (day && input.startDate && day < input.startDate) return false;
    if (day && input.endDate && (kind === 'appointment' ? day >= input.endDate : day > input.endDate)) return false;
    return kind !== 'appointment' || row.status !== 'removed';
  }).sort((a, b) => input.orderBy ? String(a[input.orderBy] || '').localeCompare(String(b[input.orderBy] || '')) * (input.orderSort === 'DESC' ? -1 : 1) : 0);
}

// Every demo request terminates here; unsupported requests never fall through to Axios.
export async function demoRequest(endpoint, payload) {
  try {
    const input = typeof payload === 'string' ? JSON.parse(payload) : (payload || {});
    const db = readDatabase();
    if (endpoint === 'get_appointment' || endpoint === 'get_dashboard') {
      const start = input.startDate || date();
      const end = input.endDate || date(input.monthWeek === 'month' ? 31 : 8);
      ensureAppointmentRange(db, start, end);
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(db));
    }
    const save = data => { localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(db)); return success(clone(data)); };
    if (endpoint === 'login') return { ...success(null), userName: 'Demo Admin', userEmail: 'demo@example.com', userToken: 'demo-only-token', userType: 'admin' };
    if (endpoint === 'register' || endpoint === 'update_user_password') return success(null);
    if (endpoint === 'get_dashboard') {
      const end = date(input.monthWeek === 'month' ? 30 : 7);
      return success({
        upcomingAppointmentList: db.appointments.filter(a => a.start_date >= date() && a.start_date <= end && a.status === 'active').sort((a, b) => a.start.localeCompare(b.start)).slice(0, 12).map(a => enrich(db, 'appointment', a)),
        nextFollowUpList: db.enquiries.filter(e => e.status === 'followup' && e.next_follow_up >= date() && e.next_follow_up <= end),
      });
    }
    if (endpoint === 'global_search') return success(['patient', 'doctor', 'invoice', 'enquiry'].map(kind => ({
      title: kind === 'enquiry' ? 'enquiries' : `${kind}s`, data: {
        heading: kind === 'invoice' ? ['uid', 'invoice_no', 'invoice_date', 'total'] : ['uid', 'name', 'mobile'],
        list: db[collections[kind][0]].map(r => enrich(db, kind, r)).filter(r => JSON.stringify(r).toLowerCase().includes(String(input.globalSearch || '').toLowerCase())),
      },
    })));
    if (endpoint === 'get_enquiry_by_mobile') return success(db.enquiries.filter(e => e.mobile === input.mobile));
    if (endpoint === 'move_to_followup') {
      const row = db.enquiries.find(e => e.uid === input.enquiryUid);
      if (!row) return failed('Demo enquiry not found');
      Object.assign(row, { status: 'followup', next_follow_up: input.nextFollowup || date(1) });
      return save(row);
    }
    if (endpoint === 'update_invoice_appointment') {
      const row = db.appointments.find(a => a.id === input.appointment_uid);
      const invoice = db.invoices.find(i => i.uid === input.invoice_uid);
      if (!row || !invoice) return failed('Demo appointment or invoice not found');
      row.invoiceUid = invoice.uid; invoice.appointment_uid = row.id;
      return save(enrich(db, 'invoice', invoice));
    }
    if (['get_invoice', 'get_appointment_invoice', 'get_patient_invoice'].includes(endpoint)) {
      const appointment = db.appointments.find(a => a.id === input.appointmentUid);
      const invoice = input.invoiceUid ? db.invoices.find(i => i.uid === input.invoiceUid) :
        input.appointmentUid ? db.invoices.find(i => i.appointment_uid === input.appointmentUid) : null;
      if (input.invoiceUid && !invoice) return failed('Demo invoice not found');
      return success(enrich(db, 'invoice', invoice || { patient_uid: input.patientUid || appointment?.patient_uid,
        appointment_uid: appointment?.id || '', title: appointment?.appt_title, consultation_fee: appointment?.consultation_fee,
        invoice_no: `DEMO-${1001 + db.invoices.length}`, invoice_item: [] }));
    }
    if (endpoint === 'get_patient_invoice_list') return success(db.invoices.filter(i => i.patient_uid === input.patientUid).map(i => enrich(db, 'invoice', i)));
    if (endpoint.includes('appointment_invoice') || endpoint === 'add_invoice') {
      const existing = db.invoices.find(i => i.uid === input.invoiceUid);
      if (endpoint.startsWith('delete_')) { db.invoices = db.invoices.filter(i => i.uid !== input.invoiceUid); db.appointments.forEach(a => { if (a.invoiceUid === input.invoiceUid) a.invoiceUid = ''; }); return save({}); }
      const appointment = db.appointments.find(a => a.id === input.appointmentUid);
      const row = normalize(db, 'invoice', input, existing || { uid: `invoice-${Date.now()}`, patient_uid: input.patientUid || appointment?.patient_uid, timestamp: new Date().toISOString() });
      if (existing) Object.assign(existing, row); else db.invoices.push(row);
      if (appointment) appointment.invoiceUid = row.uid;
      return save(enrich(db, 'invoice', row));
    }
    const match = endpoint.match(/^(get_all|list|add|update|delete|get)_(.+?)(?:_detail|_status)?$/);
    const kind = match?.[2];
    if (!match || !collections[kind]) return failed(`Demo operation not supported: ${endpoint}`);
    const [collection, idKey] = collections[kind];
    const verb = match[1];
    const id = input[idKey];
    if (verb === 'list' || verb === 'get_all' || endpoint === 'get_appointment') {
      const rows = filtered(db[collection].map(r => enrich(db, kind, r)), input, kind);
      const limit = Number(input.pageLimit) || (['appointment', 'consultation_note', 'enquiry_note'].includes(kind) || input.doctorUid ? 15 : 50);
      return success(input.page ? rows.slice((Number(input.page) - 1) * limit, Number(input.page) * limit) : rows,
        { total_list: rows.length, total_pages: Math.max(1, Math.ceil(rows.length / limit)) });
    }
    if (verb === 'get') return success(enrich(db, kind, db[collection].find(r => r.uid === id || r.id === id)));
    const existing = db[collection].find(r => r.uid === id || r.id === id);
    if (verb !== 'add' && !existing) return failed('Demo record not found');
    if (verb === 'delete') { db[collection] = db[collection].filter(r => r !== existing); return save(input); }
    const uid = existing?.uid || `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const row = normalize(db, kind, input, existing || { uid, id: uid, timestamp: new Date().toISOString() });
    if (existing) Object.assign(existing, row); else db[collection].unshift(row);
    if (kind === 'patient' && input.enquiryUid) {
      const enquiry = db.enquiries.find(e => e.uid === input.enquiryUid);
      if (enquiry) Object.assign(enquiry, { status: 'patient', patient_uid: uid });
    }
    return save(enrich(db, kind, row));
  } catch (error) { return failed(`Demo request failed: ${error.message}`); }
}
